/**
 * Paddle Billing Service (v2)
 * Handles integration with Paddle Billing for payments and subscriptions
 * 
 * Required environment variable:
 * - VITE_PADDLE_CLIENT_TOKEN: Your Paddle client-side token (from Paddle Dashboard > Developer Tools > Authentication)
 * - VITE_PADDLE_ENVIRONMENT: 'sandbox' or 'production'
 * - VITE_PADDLE_STARTER_PRICE_ID: Price ID for Starter plan
 * - VITE_PADDLE_PRO_PRICE_ID: Price ID for Pro plan
 */

import { PLANS } from '../config/plans';

// Paddle configuration from environment variables
const PADDLE_CONFIG = {
  clientToken: import.meta.env.VITE_PADDLE_CLIENT_TOKEN || '',
  environment: import.meta.env.VITE_PADDLE_ENVIRONMENT || 'sandbox',
  priceIds: {
    starter: import.meta.env.VITE_PADDLE_STARTER_PRICE_ID || '',
    pro: import.meta.env.VITE_PADDLE_PRO_PRICE_ID || '',
  },
};

class PaddleService {
  constructor() {
    this.initialized = false;
    this.initPromise = null;
  }

  /**
   * Initialize Paddle Billing
   * Call this when the app loads
   */
  async initialize() {
    // Return existing promise if already initializing
    if (this.initPromise) {
      return this.initPromise;
    }

    if (this.initialized) {
      return Promise.resolve();
    }

    this.initPromise = this._doInitialize();
    return this.initPromise;
  }

  async _doInitialize() {
    try {
      // Load Paddle.js script
      await this.loadPaddleScript();

      if (!window.Paddle) {
        throw new Error('Paddle script failed to load');
      }

      if (!PADDLE_CONFIG.clientToken) {
        console.warn('Paddle client token not configured. Set VITE_PADDLE_CLIENT_TOKEN environment variable.');
        return;
      }

      // Initialize Paddle Billing (v2)
      window.Paddle.Initialize({
        token: PADDLE_CONFIG.clientToken,
        eventCallback: this.handlePaddleEvent.bind(this),
      });

      // Set environment (sandbox for testing, production for live)
      if (PADDLE_CONFIG.environment === 'sandbox') {
        window.Paddle.Environment.set('sandbox');
      }

      this.initialized = true;
      console.log('Paddle Billing initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Paddle:', error);
      this.initPromise = null;
      throw error;
    }
  }

  /**
   * Load Paddle.js v2 script dynamically
   */
  loadPaddleScript() {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.Paddle) {
        resolve();
        return;
      }

      // Check if script is already in DOM
      const existingScript = document.querySelector('script[src*="paddle.com"]');
      if (existingScript) {
        existingScript.addEventListener('load', resolve);
        existingScript.addEventListener('error', reject);
        return;
      }

      const script = document.createElement('script');
      // Use Paddle Billing v2 script
      script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
      script.async = true;

      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Paddle script'));

      document.head.appendChild(script);
    });
  }

  /**
   * Handle Paddle events
   */
  handlePaddleEvent(event) {
    console.log('Paddle event:', event);

    switch (event.name) {
      case 'checkout.closed':
        window.dispatchEvent(new CustomEvent('paddle-checkout-closed', { detail: event.data }));
        break;

      case 'checkout.completed':
        console.log('Checkout completed:', event.data);
        window.dispatchEvent(new CustomEvent('paddle-checkout-completed', { detail: event.data }));
        break;

      case 'checkout.customer.created':
        console.log('Customer created:', event.data);
        break;

      case 'checkout.payment.initiated':
        console.log('Payment initiated');
        break;

      case 'checkout.payment.failed':
        console.log('Payment failed:', event.data);
        window.dispatchEvent(new CustomEvent('paddle-payment-failed', { detail: event.data }));
        break;

      default:
        console.log('Unhandled Paddle event:', event.name);
    }
  }

  /**
   * Open checkout for a NEW subscription
   * Use this only for users without an existing subscription
   * @param {string} planId - 'starter' or 'pro'
   * @param {Object} user - Firebase user object with uid and email
   * @param {Object} options - Additional checkout options
   */
  async openCheckout(planId, user, options = {}) {
    await this.initialize();

    if (!window.Paddle) {
      throw new Error('Paddle is not initialized');
    }

    const priceId = PADDLE_CONFIG.priceIds[planId];
    if (!priceId) {
      throw new Error(`No price ID configured for plan: ${planId}. Set VITE_PADDLE_${planId.toUpperCase()}_PRICE_ID`);
    }

    const plan = PLANS[planId];
    if (!plan) {
      throw new Error(`Invalid plan ID: ${planId}`);
    }

    try {
      // Paddle Billing v2 checkout configuration
      const checkoutConfig = {
        items: [
          {
            priceId: priceId,
            quantity: 1,
          },
        ],
        customer: {
          email: user.email,
        },
        // Custom data is passed through to webhooks
        customData: {
          userId: user.uid,
          userEmail: user.email,
          planId: planId,
        },
        settings: {
          displayMode: 'overlay',
          theme: 'dark',
          locale: 'en',
          successUrl: options.successUrl || window.location.origin + '/billing?success=true',
        },
      };

      // Open Paddle checkout
      window.Paddle.Checkout.open(checkoutConfig);
    } catch (error) {
      console.error('Error opening Paddle checkout:', error);
      throw error;
    }
  }

  /**
   * Update an existing subscription (upgrade/downgrade)
   * Calls backend API which uses Paddle API to update the subscription
   * @param {string} subscriptionId - Existing Paddle subscription ID
   * @param {string} newPlanId - 'starter' or 'pro'
   * @param {Object} user - Firebase user object (for auth token)
   */
  async updateSubscription(subscriptionId, newPlanId, user) {
    if (!subscriptionId) {
      throw new Error('Subscription ID is required for updates');
    }

    if (!user) {
      throw new Error('User is required for authentication');
    }

    try {
      // Get Firebase auth token
      const idToken = await user.getIdToken();

      // Call backend API to update subscription via Paddle API
      const response = await fetch('/api/update-subscription', {
        method: 'POST',
        headers: {
          'X-Firebase-Token': idToken,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscriptionId,
          newPlanId,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to update subscription');
      }

      // Dispatch success event so UI can refresh
      window.dispatchEvent(new CustomEvent('paddle-checkout-completed', {
        detail: {
          type: 'subscription_update',
          subscriptionId,
          newPlan: newPlanId,
        }
      }));

      return data;
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  }

  /**
   * Get the price ID for a plan
   * @param {string} planId - Plan ID
   */
  getPriceId(planId) {
    return PADDLE_CONFIG.priceIds[planId] || null;
  }

  /**
   * Get plan details including price ID
   * @param {string} planId - Plan ID
   */
  getPlanWithPriceId(planId) {
    const plan = PLANS[planId];
    if (!plan) return null;

    return {
      ...plan,
      priceId: PADDLE_CONFIG.priceIds[planId],
    };
  }

  /**
   * Check if Paddle is configured
   */
  isConfigured() {
    return !!(
      PADDLE_CONFIG.clientToken &&
      PADDLE_CONFIG.priceIds.starter &&
      PADDLE_CONFIG.priceIds.pro
    );
  }

  /**
   * Get configuration status for debugging
   */
  getConfigStatus() {
    return {
      clientToken: !!PADDLE_CONFIG.clientToken,
      environment: PADDLE_CONFIG.environment,
      starterPriceId: !!PADDLE_CONFIG.priceIds.starter,
      proPriceId: !!PADDLE_CONFIG.priceIds.pro,
    };
  }

  /**
   * Open subscription management
   * Note: Paddle Billing v2 sends management links via email to customers.
   * For self-service portal, you'd need to call Paddle API to generate a portal session.
   * This opens Paddle's support page as a fallback.
   */
  openBillingPortal(subscriptionId) {
    // Paddle Billing doesn't have a direct URL pattern like Classic.
    // Customers receive management links via email from Paddle.
    // For a proper implementation, you'd call the Paddle API to create a portal session.
    const paddleSupport = 'https://paddle.com';
    window.open(paddleSupport, '_blank');

    // Show user instructions
    alert(
      'To manage your subscription, please check your email for a link from Paddle, ' +
      'or contact support if you need assistance.'
    );
  }

  /**
   * Cancel subscription
   * Note: Paddle Billing v2 cancellations are handled via customer portal or API.
   * Customers receive a cancellation link via email.
   */
  cancelSubscription(subscriptionId) {
    alert(
      'To cancel your subscription, please check your email for your subscription management link from Paddle, ' +
      'or contact us at support@yourapp.com and we\'ll help you cancel.'
    );
  }
}

// Export singleton instance
export const paddleService = new PaddleService();

export default paddleService;
